// File: frontend/src/pages/KnowledgeGraph/components/ForceGraph3DView.tsx
import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { Box, Chip, CircularProgress, Tooltip, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { KGClusterResponse, MAX_GRAPH_NODES, ENTITY_TYPE_COLORS, ENTITY_TYPE_LABELS } from '../types';
import { escapeHtml, hexToRgba } from '../utils';
import GraphControls, { LegendItem } from './GraphControls';

// Lazy-load heavy 3D deps
let ForceGraph3D: any = null;
let SpriteText: any = null;

async function load3DDeps() {
  if (!ForceGraph3D) {
    const [fgModule, stModule] = await Promise.all([
      import('react-force-graph-3d'),
      import('three-spritetext'),
    ]);
    ForceGraph3D = fgModule.default;
    SpriteText = stModule.default;
  }
  return { ForceGraph3D, SpriteText };
}

// Without a selection, only the top-N nodes by report count carry a label
const TOP_LABEL_COUNT = 10;

interface ForceGraph3DViewProps {
  cluster: KGClusterResponse | null;
  loading: boolean;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  onExpandNode: (nodeId: string) => void;
  depth: number;
  onDepthChange: (d: number) => void;
  showInferred: boolean;
  onShowInferredChange: (v: boolean) => void;
  truncated: boolean;
  totalNodeCount: number;
  hiddenTypes: string[];
  onToggleType: (type: string) => void;
  legendItems: LegendItem[];
}

interface FGNode {
  id: string;
  name: string;
  type: string;
  report_count: number;
  color: string;
  // Force-graph adds x,y,z at runtime
  x?: number;
  y?: number;
  z?: number;
}

interface FGLink {
  source: string | FGNode;
  target: string | FGNode;
  type: string;
  edge_kind: string;
  confidence: number;
  mention_count: number;
  color: string;
}

const EDGE_COLOR_OBSERVED = 'rgba(156,163,175,0.5)'; // gray
const EDGE_COLOR_INFERRED = 'rgba(77,208,225,0.7)';  // theme cyan
const EDGE_COLOR_DIMMED = 'rgba(107,114,128,0.08)';

// force-graph mutates link.source/target from id strings into node objects
function linkEndId(end: string | FGNode): string {
  return typeof end === 'object' ? end.id : end;
}

const ForceGraph3DView: React.FC<ForceGraph3DViewProps> = ({
  cluster,
  loading,
  selectedNodeId,
  onSelectNode,
  onExpandNode,
  depth,
  onDepthChange,
  showInferred,
  onShowInferredChange,
  truncated,
  totalNodeCount,
  hiddenTypes,
  onToggleType,
  legendItems,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const fgRef = useRef<any>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [depsLoaded, setDepsLoaded] = useState(false);

  // Load 3D dependencies
  useEffect(() => {
    if (isMobile) return;
    load3DDeps().then(() => setDepsLoaded(true));
  }, [isMobile]);

  // Convert cluster data to force-graph format (legend filter applied here)
  const graphData = useMemo(() => {
    if (!cluster) return { nodes: [] as FGNode[], links: [] as FGLink[] };

    const nodes: FGNode[] = cluster.nodes
      .filter((n) => !hiddenTypes.includes(n.type))
      .map((n) => ({
        id: n.id,
        name: n.name,
        type: n.type,
        report_count: n.report_count || 1,
        color: ENTITY_TYPE_COLORS[n.type] || '#6b7280',
      }));

    const nodeIds = new Set(nodes.map((n) => n.id));
    const links: FGLink[] = cluster.edges
      .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
      .map((e) => ({
        source: e.source,
        target: e.target,
        type: e.type,
        edge_kind: e.edge_kind,
        confidence: e.confidence || 0,
        mention_count: e.mention_count || 1,
        color: e.edge_kind === 'INFERRED_REL' ? EDGE_COLOR_INFERRED : EDGE_COLOR_OBSERVED,
      }));

    return { nodes, links };
  }, [cluster, hiddenTypes]);

  // Neighborhood of the selected node (selection + direct neighbors)
  const highlightIds = useMemo(() => {
    if (!selectedNodeId) return null;
    const set = new Set<string>([selectedNodeId]);
    graphData.links.forEach((l) => {
      const s = linkEndId(l.source);
      const t = linkEndId(l.target);
      if (s === selectedNodeId) set.add(t);
      if (t === selectedNodeId) set.add(s);
    });
    return set;
  }, [graphData, selectedNodeId]);

  // Top nodes by report count keep a label when nothing is selected
  const topLabelIds = useMemo(() => {
    return new Set(
      [...graphData.nodes]
        .sort((a, b) => b.report_count - a.report_count)
        .slice(0, TOP_LABEL_COUNT)
        .map((n) => n.id),
    );
  }, [graphData]);

  // Node click handler
  const handleNodeClick = useCallback(
    (node: FGNode) => {
      onSelectNode(node.id);
      // Focus camera on clicked node
      const fg = fgRef.current;
      if (fg && node.x !== undefined && node.y !== undefined && node.z !== undefined) {
        const distance = 200;
        const distRatio = 1 + distance / Math.hypot(node.x, node.y, node.z || 1);
        fg.cameraPosition(
          { x: node.x * distRatio, y: node.y * distRatio, z: node.z! * distRatio },
          { x: node.x, y: node.y, z: node.z },
          1000,
        );
      }
    },
    [onSelectNode],
  );

  // Background click handler
  const handleBackgroundClick = useCallback(() => {
    onSelectNode(null);
  }, [onSelectNode]);

  // Dim nodes outside the selected neighborhood
  const nodeColor = useCallback(
    (node: FGNode) => {
      if (highlightIds && !highlightIds.has(node.id)) return hexToRgba(node.color, 0.15);
      return node.color;
    },
    [highlightIds],
  );

  // Dim links not touching the selected node
  const linkColor = useCallback(
    (link: FGLink) => {
      if (!highlightIds || !selectedNodeId) return link.color;
      const touches = linkEndId(link.source) === selectedNodeId || linkEndId(link.target) === selectedNodeId;
      return touches ? link.color : EDGE_COLOR_DIMMED;
    },
    [highlightIds, selectedNodeId],
  );

  // Node ThreeObject: SpriteText label for selection neighborhood / top nodes only
  const nodeThreeObject = useCallback(
    (node: FGNode) => {
      if (!SpriteText) return undefined;
      const labeled = highlightIds ? highlightIds.has(node.id) : topLabelIds.has(node.id);
      if (!labeled) return undefined;
      const sprite = new SpriteText(node.name.length > 20 ? node.name.slice(0, 18) + '…' : node.name);
      sprite.color = node.color;
      sprite.textHeight = 4;
      sprite.fontWeight = 'bold';
      sprite.backgroundColor = 'rgba(0,0,0,0.3)';
      sprite.borderRadius = 2;
      sprite.padding = 1;
      sprite.center = { x: 0.5, y: -0.6 }; // shift above the sphere
      return sprite;
    },
    [highlightIds, topLabelIds],
  );

  // Link width based on mention_count
  const linkWidth = useCallback((link: FGLink) => {
    return Math.max(0.5, Math.min(link.mention_count * 0.5, 3));
  }, []);

  // Camera controls
  const zoomBy = useCallback((factor: number) => {
    const fg = fgRef.current;
    if (!fg) return;
    const pos = fg.camera().position;
    fg.cameraPosition({ x: pos.x * factor, y: pos.y * factor, z: pos.z * factor }, undefined, 300);
  }, []);

  const handleZoomIn = useCallback(() => zoomBy(1 / 1.3), [zoomBy]);
  const handleZoomOut = useCallback(() => zoomBy(1.3), [zoomBy]);
  const handleFit = useCallback(() => {
    fgRef.current?.zoomToFit(400, 60);
  }, []);

  // Dimensions
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };
    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Mobile fallback
  if (isMobile) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <HubIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.4, mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          3D graph visualization is optimized for desktop. Use search and entity details on mobile.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', minHeight: 400 }}>
      {/* Controls overlay */}
      <GraphControls
        depth={depth}
        onDepthChange={onDepthChange}
        showInferred={showInferred}
        onShowInferredChange={onShowInferredChange}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFit={handleFit}
        legendItems={legendItems}
        hiddenTypes={hiddenTypes}
        onToggleType={onToggleType}
      />

      {/* Truncation chip */}
      {truncated && (
        <Tooltip
          title={`Showing the top ${MAX_GRAPH_NODES} of ${totalNodeCount} connections, sorted by report count. Use depth 1 or hide entity types from the legend for full detail.`}
        >
          <Chip
            icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
            label={`${MAX_GRAPH_NODES}/${totalNodeCount} shown`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              zIndex: 10,
              bgcolor: (t) => alpha(t.palette.background.paper, 0.9),
              backdropFilter: 'blur(6px)',
            }}
          />
        </Tooltip>
      )}

      {/* Loading overlay */}
      {(loading || !depsLoaded) && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.background.default, 0.6),
            zIndex: 5,
            borderRadius: 2,
          }}
        >
          <CircularProgress />
          {!depsLoaded && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Loading 3D engine…
            </Typography>
          )}
        </Box>
      )}

      {/* Empty state */}
      {!cluster && !loading && depsLoaded && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <HubIcon sx={{ fontSize: 120, color: 'text.secondary', opacity: 0.15, mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Select an entity above to explore its connections in 3D
          </Typography>
        </Box>
      )}

      {/* 3D Graph container */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 400,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        {depsLoaded && ForceGraph3D && cluster && (
          <ForceGraph3D
            ref={fgRef}
            graphData={graphData}
            width={dimensions.width}
            height={dimensions.height}
            backgroundColor="#1a1d2e"
            nodeId="id"
            nodeLabel={(node: FGNode) =>
              `<b>${escapeHtml(node.name)}</b><br/>Type: ${escapeHtml(ENTITY_TYPE_LABELS[node.type] || node.type)}<br/>Reports: ${node.report_count}`
            }
            nodeColor={nodeColor}
            nodeVal={(node: FGNode) => Math.max(2, Math.sqrt(node.report_count) * 2)}
            nodeOpacity={0.9}
            nodeThreeObjectExtend={true}
            nodeThreeObject={nodeThreeObject}
            linkSource="source"
            linkTarget="target"
            linkColor={linkColor}
            linkWidth={linkWidth}
            linkOpacity={0.6}
            linkDirectionalArrowLength={4}
            linkDirectionalArrowRelPos={0.9}
            linkDirectionalArrowColor={linkColor}
            linkLabel={(link: FGLink) =>
              `${escapeHtml(link.type || 'related')} (${link.edge_kind === 'INFERRED_REL' ? `conf: ${link.confidence.toFixed(2)}` : `mentions: ${link.mention_count}`})`
            }
            onNodeClick={handleNodeClick}
            onBackgroundClick={handleBackgroundClick}
            showNavInfo={false}
            enableNodeDrag={true}
            warmupTicks={50}
            cooldownTicks={100}
          />
        )}
      </Box>
    </Box>
  );
};

export default ForceGraph3DView;
